import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ProductCard } from "./Home";
import { Loader, EmptyState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";

const SORTS = [
  { v: "popular", l: "Popular" }, { v: "highest_margin", l: "Highest Margin" },
  { v: "lowest_price", l: "Lowest Price" }, { v: "newest", l: "Newest" },
];

export default function Marketplace() {
  const [products, setProducts] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("popular");
  const [cats, setCats] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/marketplace/categories").then((r) => setCats(r.data.categories)); }, []);

  const load = useCallback(async () => {
    setProducts(null);
    const params = new URLSearchParams({ sort, page, limit: 12 });
    if (q) params.set("q", q);
    if (category !== "all") params.set("category", category);
    const { data } = await api.get(`/marketplace/products?${params}`);
    setProducts(data.products); setTotal(data.total);
  }, [q, category, sort, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-forest">Marketplace</h1>
        <p className="text-sm text-ink/60">{total} products from verified manufacturers</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-msage" />
          <Input data-testid="market-search" placeholder="Search products, brands, SKU…" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger data-testid="market-category" className="sm:w-44"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="market-sort" className="sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {products === null ? <Loader /> : products.length === 0 ? (
        <EmptyState title="No products found" hint="Try a different search or category." />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      )}

      {total > 12 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button data-testid="market-prev" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <span className="num text-sm">Page {page} / {Math.ceil(total / 12)}</span>
          <Button data-testid="market-next" variant="outline" disabled={page >= Math.ceil(total / 12)} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
